import type { Schema } from '@cli-types/core-engine/schema.js'
import type {
  BuiltInEntityFilterKey,
  ResolvedEntityFilter
} from '@cli-types/next-adapter/generators.js'
import { BUILT_IN_FILTER_KEYS } from '@cli-constants/schema-codegen.js'
import { ensureFilterableField } from './ensure-filterable-field.js'
import { getEntityFieldContext } from './get-entity-field-context.js'
import { getSchemaFieldByName } from './get-schema-field-by-name.js'

export function resolveEntityFilters(schema: Schema): ResolvedEntityFilter[] {
  const { allDbFields } = getEntityFieldContext(schema)
  const resolved: ResolvedEntityFilter[] = []
  const seenKeys = new Set<string>()
  const reservedFields = new Set<string>()

  const publishedField = getSchemaFieldByName(allDbFields, 'published')
  if (publishedField?.type === 'boolean') {
    resolved.push({
      key: 'status',
      field: 'published',
      label: 'Status',
      type: 'select',
      source: 'builtin',
      options: [
        { label: 'Published', value: 'published' },
        { label: 'Draft', value: 'draft' }
      ],
      fieldType: publishedField.type,
      builtIn: 'status'
    })
    seenKeys.add('status')
    reservedFields.add('published')
  }

  const createdAtField = getSchemaFieldByName(allDbFields, 'createdAt')
  if (createdAtField) {
    resolved.push({
      key: 'createdAt',
      field: 'createdAt',
      label: 'Created',
      type: 'date-range',
      source: 'builtin',
      options: [],
      fieldType: createdAtField.type,
      builtIn: 'createdAt'
    })
    seenKeys.add('createdAt')
    reservedFields.add('createdAt')
  }

  for (const filter of schema.filters || []) {
    const key = filter.key || filter.field

    if (BUILT_IN_FILTER_KEYS.has(key as BuiltInEntityFilterKey)) {
      throw new Error(
        `Schema "${schema.name}" defines a filter with reserved key "${key}". Choose a different filter key.`
      )
    }

    if (seenKeys.has(key)) {
      throw new Error(
        `Schema "${schema.name}" defines duplicate filter key "${key}". Filter keys must be unique.`
      )
    }

    if (reservedFields.has(filter.field)) {
      throw new Error(
        `Schema "${schema.name}" defines a filter for "${filter.field}", but that field already has a built-in filter.`
      )
    }

    const field = ensureFilterableField(
      getSchemaFieldByName(allDbFields, filter.field),
      filter,
      schema.name
    )

    const type = filter.type ?? 'select'
    if (type === 'boolean' && field.type !== 'boolean') {
      throw new Error(
        `Schema "${schema.name}" defines boolean filter "${key}", but "${filter.field}" is not a boolean field.`
      )
    }

    const source =
      type === 'boolean'
        ? 'static'
        : (filter.source ?? (filter.options && filter.options.length > 0 ? 'static' : 'distinct'))

    const options =
      type === 'boolean'
        ? [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' }
          ]
        : source === 'static'
          ? (filter.options ?? [])
          : []

    if (type === 'select' && source === 'static' && options.length === 0) {
      throw new Error(`Schema "${schema.name}" defines static filter "${key}" without any options.`)
    }

    resolved.push({
      key,
      field: filter.field,
      label: filter.label,
      type,
      source,
      options,
      fieldType: field.type
    })
    seenKeys.add(key)
  }

  return resolved
}
